import { Layout } from 'antd';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { HeraldryRoundResultModal } from '../../components/HeraldryRoundResultModal';
import { BackToModesLink, RoundStatus } from '../../components/quiz';
import { HERALDRY_TOTAL_ROUNDS_MAX } from '../../constants/game';
import gameModes from '../../enums/modes';
import useGameMenuResize from '../../hooks/useGameMenuResize';
import { setCurrentGame, setTotalRoundCounter, setTotalRoundScore } from '../../redux/actions/game';
import { setLastResult } from '../../redux/actions/result';
import { HeraldryResultT } from '../../types/heraldry';
import { CzechCity } from '../../types/places';
import { getRandomCzechPlaceWithCoatOfArms } from '../../util';
import { shuffleArray } from '../../util/array';

const { Content } = Layout;

type HeraldryOptionState = 'idle' | 'correct' | 'wrong' | 'muted';

const HeraldryGamePanel = styled.div`
    width: 100%;
    max-width: 900px;
    display: grid;
    gap: 28px;
    padding: 20px 16px 30px;
`;

const CoaContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`;

const HeraldryEmblem = styled.img`
    display: block;
    width: 100%;
    max-width: 420px;
    max-height: 48vh;
    object-fit: contain;

    @media (max-width: 640px) {
        max-width: 300px;
        max-height: 38vh;
    }
`;

const HeraldryOptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(170px, 240px));
    justify-content: center;
    gap: 16px;
    width: 100%;

    @media (max-width: 820px) {
        grid-template-columns: repeat(2, minmax(170px, 240px));
    }

    @media (max-width: 560px) {
        grid-template-columns: minmax(0, 320px);
    }
`;

const optionStateStyles = (state: HeraldryOptionState) => {
    if (state === 'correct') {
        return css`
            background: #f6ffed;
            border-color: #52c41a;
            color: #155724;
        `;
    }
    if (state === 'wrong') {
        return css`
            background: #fff1f0;
            border-color: #ff4d4f;
            color: #721c24;
        `;
    }
    if (state === 'muted') {
        return css`
            background: #fafafa;
            border-color: #e8e8e8;
            color: #8c8c8c;
            opacity: 0.72;
        `;
    }
    return css`
        background: #ffffff;
        border-color: #e8e8e8;
        color: #1f1f1f;
    `;
};

const HeraldryOptionCard = styled.button<{ $state: HeraldryOptionState }>`
    min-height: 104px;
    padding: 18px 20px;
    border: 2px solid;
    border-radius: 12px;
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.04);
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    font-family: inherit;
    text-align: center;
    transition: transform 0.14s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
    ${({ $state }) => optionStateStyles($state)}

    &:hover {
        transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
        border-color: ${({ disabled, $state }) => (disabled || $state !== 'idle' ? undefined : '#c80707')};
        box-shadow: ${({ disabled }) => (disabled ? undefined : '0 10px 28px rgba(0, 0, 0, 0.07)')};
    }

    &:focus-visible {
        outline: 3px solid #ffd6d6;
        outline-offset: 2px;
    }
`;

const OptionTitle = styled.div`
    font-size: 18px;
    line-height: 1.25;
    font-weight: 700;
`;

const OptionMeta = styled.div`
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.35;
    color: #737373;
`;

const getOptionState = (isRevealed: boolean, isCorrect: boolean, isThisCard: boolean): HeraldryOptionState => {
    if (!isRevealed) return 'idle';
    if (isCorrect) return 'correct';
    if (isThisCard) return 'wrong';
    return 'muted';
};

export const HeraldryGame = () => {
    const dispatch = useDispatch();
    // @ts-ignore
    const currentGame = useSelector(state => state.game.currentGame);
    const [resultModalVisible, setResultModalVisible] = useState(false);
    const [guessedCity, setGuessedCity] = useState<CzechCity | null>(null);
    const [allGuessedCities, setAllGuessedCities] = useState<HeraldryResultT[]>([]);
    const [isRevealed, setIsRevealed] = useState(false);
    useGameMenuResize();

    const { mode, round, city, totalScore } = currentGame;

    const shuffledCitiesToGuessArray = useMemo(() => {
        if (city) {
            const randomFakeCity1 = getRandomCzechPlaceWithCoatOfArms([city]);
            const randomFakeCity2 = getRandomCzechPlaceWithCoatOfArms([city, randomFakeCity1]);
            const cardCities = [city, randomFakeCity1, randomFakeCity2];
            return shuffleArray(cardCities);
        }
        return null;
    }, [city]);

    const handleGuessHeraldry = (cityWhichWasGuessed: CzechCity) => {
        if (isRevealed) return; // Zabránit dvojitému kliknutí

        const roundResultScore = cityWhichWasGuessed.kod === city.kod ? 1 : 0;
        setGuessedCity(cityWhichWasGuessed);
        setIsRevealed(true);
        // @ts-ignore
        dispatch(setTotalRoundScore(totalScore + roundResultScore));
        setAllGuessedCities([
            ...allGuessedCities,
            {
                guessed: !!roundResultScore,
                city,
                cityWhichWasGuessed,
            },
        ]);
        // Krátká pauza před zobrazením modalu, aby uživatel viděl výsledek
        setTimeout(() => {
            setResultModalVisible(true);
        }, 800);
    };

    const handleGuessNextRound = () => {
        const randomCity = getRandomCzechPlaceWithCoatOfArms();

        setResultModalVisible(false);
        setIsRevealed(false);
        setGuessedCity(null);
        dispatch(
            // @ts-ignore
            setCurrentGame({
                city: randomCity,
            }),
        );
        // @ts-ignore
        dispatch(setTotalRoundCounter(round + 1));
    };

    const handleShowResult = () => {
        setResultModalVisible(false);
        dispatch(
            // @ts-ignore
            setLastResult({
                totalScore,
                mode: gameModes.heraldry,
                guessedCoatOfArms: allGuessedCities,
            }),
        );
    };

    if (mode === gameModes.heraldry && city && shuffledCitiesToGuessArray) {
        return (
            <Content>
                <HeraldryGamePanel>
                    <BackToModesLink />
                    <RoundStatus
                        round={round}
                        score={totalScore}
                        maxScore={HERALDRY_TOTAL_ROUNDS_MAX}
                        totalRounds={HERALDRY_TOTAL_ROUNDS_MAX}
                        scoreLabel="Správné odpovědi"
                        scoreUnit="správně"
                    />
                    <CoaContainer>
                        <HeraldryEmblem src={city.coatOfArms} alt={`Znak obce ${city.obec}`} />
                        <HeraldryOptionsGrid>
                            {shuffledCitiesToGuessArray.map((cardCity: CzechCity) => {
                                const { obec, okres, kraj, kod } = cardCity;
                                const isThisCard = guessedCity?.kod === kod;
                                const isCorrectCity = city.kod === kod;
                                const optionState = getOptionState(isRevealed, isCorrectCity, isThisCard);

                                return (
                                    <HeraldryOptionCard
                                        key={kod}
                                        type="button"
                                        $state={optionState}
                                        disabled={isRevealed}
                                        onClick={() => handleGuessHeraldry(cardCity)}
                                    >
                                        <OptionTitle>
                                            {isRevealed && isCorrectCity && '✓ '}
                                            {isRevealed && isThisCard && !isCorrectCity && '✗ '}
                                            {obec}
                                        </OptionTitle>
                                        <OptionMeta>
                                            {okres} ({kraj})
                                        </OptionMeta>
                                    </HeraldryOptionCard>
                                );
                            })}
                        </HeraldryOptionsGrid>
                    </CoaContainer>
                </HeraldryGamePanel>
                {guessedCity && (
                    <HeraldryRoundResultModal
                        visible={resultModalVisible}
                        onGuessNextRound={handleGuessNextRound}
                        onShowResult={handleShowResult}
                        city={city}
                        guessedCity={guessedCity}
                    />
                )}
            </Content>
        );
    }

    return (
        <Redirect
            to={{
                pathname: '/',
            }}
        />
    );
};
